import { EApplicantType, EContractStatus, EFinancingProgram, ELanguage, EOriginSystemId } from '@if/core/types'
import { formatPhone } from '@if/core/utils'
import { Action, ActionList, DataField, IconTile, StrongValue, SubsectionTitle, useConfirmDialog } from '@if/mui'
import { Grid, Stack } from '@mui/material'
import { useDeleteCoapplicant } from '@src/data/api/credit-api/credit-api'
import { ApplicantDto, CreditApplication } from '@src/types/CreditApplicationSchema'
import { Globe, Mail, Pencil, Phone, Trash2, User, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Props {
  applicant: ApplicantDto
  applicantTitle: string
  applicantType: EApplicantType
  creditApplication: CreditApplication
  disabled: boolean
  financingProgramId: EFinancingProgram
}

const ApplicantInfoBox = ({
  applicant,
  applicantTitle,
  applicantType,
  creditApplication,
  disabled,
  financingProgramId,
}: Props) => {
  const { t } = useTranslation()
  const [deleteCoapplicant] = useDeleteCoapplicant()

  const isContractComplete = creditApplication?.contract?.status === EContractStatus.Completed
  const isMerchantDashboardOrigin = creditApplication?.originSystemId === EOriginSystemId.MerchantDashboard
  const isCoApplicant = applicantType === EApplicantType.CoApplicant
  const preferredLanguage = applicant.languageId === ELanguage.English ? t('common.english') : t('common.french')

  const confirmDelete = useConfirmDialog({
    title: t('viewCreditApplication.deleteCoapplicant'),
    message: t('viewCreditApplication.confirmDeletionMessage'),
    mode: 'destructive',
    onConfirm: async () => {
      await deleteCoapplicant({ creditApplicationId: creditApplication.id, financingProgramId })
    },
  })

  const menuActions: Action[] = []
  if (isMerchantDashboardOrigin) {
    if (!disabled) {
      menuActions.push({
        icon: Pencil,
        label: t('common.edit'),
        href: `/credits/${financingProgramId}/${creditApplication.id}/edit#${isCoApplicant ? 'coapplicant' : 'applicant'}`,
      })
    }
    if (isCoApplicant && !isContractComplete) {
      menuActions.push({
        icon: Trash2,
        label: t('common.delete'),
        tone: 'destructive',
        onClick: () => void confirmDelete.open(),
      })
    }
  }

  const fields = [
    { icon: User, label: t('viewCreditApplication.name'), value: `${applicant.firstName} ${applicant.lastName}` },
    { icon: Phone, label: t('viewCreditApplication.phone'), value: formatPhone(applicant.cellPhone) },
    { icon: Mail, label: t('viewCreditApplication.email'), value: applicant.email },
    { icon: Globe, label: t('viewCreditApplication.preferredLanguage'), value: preferredLanguage },
  ]

  return (
    <Stack spacing={1.5} sx={{ height: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconTile icon={isCoApplicant ? Users : User} color="primary" size="sm" />
        <SubsectionTitle>{applicantTitle}</SubsectionTitle>
        <ActionList
          actions={menuActions}
          variant="icon"
          menuLabel={t('viewCreditApplication.actions')}
          sx={{ ml: 'auto' }}
        />
      </Stack>
      <Grid container spacing={1.5}>
        {fields.map((field) => (
          <Grid key={field.label} size={{ xs: 12, sm: 6 }}>
            <DataField
              icon={field.icon}
              label={field.label}
              labelVariant="eyebrow"
              value={<StrongValue>{field.value}</StrongValue>}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  )
}

export default ApplicantInfoBox
